import { supabase } from '@/shared/lib/supabase';

/**
 * Cross-customer pending-orders read service.
 * Backed by v_admin_pending_orders (RLS via is_staff() in the view body).
 */

export type ServiceOrderStatus =
  | 'pending_payment'
  | 'paid'
  | 'processing'
  | 'failed_retryable'
  | 'awaiting_action';

// Mirrors the live `invoice_lifecycle_state_check` — all 8 permitted states.
// `pending_payment` added 2026-07-27 (see adminInvoiceService for the why).
export type InvoiceLifecycleState =
  | 'draft' | 'pending_payment' | 'unpaid' | 'grace' | 'paid' | 'suspended' | 'refunded' | 'cancelled';

export interface AdminPendingOrderRow {
  service_order_id: string;
  sku_code: string;
  order_status: ServiceOrderStatus | string;
  primary_fqdn: string | null;
  fqdn_mode: 'register' | 'existing' | 'later' | null;
  order_created_at: string;
  customer_id: string;
  customer_email: string | null;
  customer_name: string | null;
  customer_company: string | null;
  plan_name: string | null;
  plan_slug: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  invoice_total: number | null;
  invoice_state: InvoiceLifecycleState | null;
  invoice_due_date: string | null;
  invoice_payment_token: string | null;
  bundled_domain: string | null;
  bundled_domain_status: string | null;
}

export const adminPendingOrdersService = {
  async listAll(): Promise<AdminPendingOrderRow[]> {
    const { data, error } = await supabase
      .from('v_admin_pending_orders')
      .select(
        'service_order_id, sku_code, order_status, primary_fqdn, fqdn_mode, order_created_at, customer_id, customer_email, customer_name, customer_company, plan_name, plan_slug, invoice_id, invoice_number, invoice_total, invoice_state, invoice_due_date, invoice_payment_token, bundled_domain, bundled_domain_status'
      )
      .order('order_created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as AdminPendingOrderRow[];
  },

  /**
   * Build the customer-facing PayFast URL for an unpaid invoice. Mirrors the
   * format used in service-order-confirm's notification payload + EmailLayout.
   * Used in the admin "Copy PayFast link" action.
   */
  buildPayUrl(invoiceId: string, paymentToken: string | null): string {
    const portalBase = 'https://portal.sighthost.online';
    if (paymentToken) {
      return `${portalBase}/pay/${invoiceId}?token=${paymentToken}`;
    }
    return `${portalBase}/app/billing/invoices/${invoiceId}`;
  },
};
